import _ from 'lodash'
import type { OrgAiIntegrationUsage } from '../types'
import { sumTokens } from '../constants/aiTokens'

// Plain-English outcome from the before/after snapshots, so "$0" / "no change" /
// "empty" aren't misread as bugs. No status record exists, so a failed collection
// just surfaces as "empty"/"unchanged".
export type BackfillOutcome =
  | 'filled'
  | 'recollected'
  | 'filled_zero'
  | 'unchanged'
  | 'empty'
  | 'unconfirmed'

export interface BackfillOutcomeResult {
  outcome: BackfillOutcome
  label: string
  tone: 'success' | 'neutral' | 'warning'
  description: string
  costRecovered: number
}

const TONE_CLASS: Record<BackfillOutcomeResult['tone'], string> = {
  success: 'text-green-600',
  neutral: 'text-foreground',
  warning: 'text-amber-600'
}

// Outcome → text-color class; any recovered cost reads as success (a re-collection
// that restated some values can still recover spend). Falls back for the
// string-typed tone on S3-loaded records (whose type isn't narrowed to the union).
export const toneClassFor = (outcome?: {
  tone?: string
  costRecovered?: number
}): string =>
  (outcome?.costRecovered ?? 0) > 0
    ? TONE_CLASS.success
    : (TONE_CLASS[outcome?.tone as BackfillOutcomeResult['tone']] ??
      'text-foreground')

const cost = (u?: OrgAiIntegrationUsage): number => u?.totals?.cost_usd ?? 0
const modelCount = (u?: OrgAiIntegrationUsage): number => u?.models?.length ?? 0

// Non-cost signals a backfill can recover: credits (Kiro) and the interaction/
// line counters that adoption tools (e.g. Amazon Q) report instead of cost/models.
const ACTIVITY_KEYS: (keyof OrgAiIntegrationUsage['totals'])[] = [
  'credits_used',
  'prompts',
  'threads',
  'user_interactions',
  'code_generations',
  'code_acceptances',
  'inline_suggestions_count',
  'inline_acceptance_count',
  'chat_messages_sent',
  'lines_added',
  'lines_removed',
  'distinct_users'
]

// Token counts, kept out of ACTIVITY_KEYS so the activity wording below stays accurate.
// Most token-bearing rows carry no cost (Copilot only recently started reporting counts),
// so a window can gain tokens while cost and models sit still. Without this term such a
// run polls to cap_seconds and then misreports "No change".
export const tokenSum = (u?: OrgAiIntegrationUsage | null): number =>
  sumTokens(u?.totals)

// Fast attribution lives on models, not totals, so a re-collect that only adds or
// corrects the tier split leaves every totals-based signal flat. Without these terms
// such a run polls to cap_seconds and then misreports "No change".
const fastCost = (u?: OrgAiIntegrationUsage): number =>
  _.sumBy(u?.models ?? [], m => m.fast_cost_usd ?? 0)
const fastTokens = (u?: OrgAiIntegrationUsage): number =>
  _.sumBy(u?.models ?? [], m => m.fast_tokens ?? 0)

// Every signal a backfill can move, compared one by one. Summing them would let a drop
// in one metric (Kiro credits restated down) cancel out gains in another (chat messages,
// lines added) and report "No change" on a run that clearly moved data.
const SIGNALS: ((u?: OrgAiIntegrationUsage) => number)[] = [
  cost,
  modelCount,
  tokenSum,
  fastCost,
  fastTokens,
  ...ACTIVITY_KEYS.map(k => (u?: OrgAiIntegrationUsage) => u?.totals?.[k] ?? 0)
]

const signalDeltas = (
  before?: OrgAiIntegrationUsage,
  after?: OrgAiIntegrationUsage
): { up: number; down: number } => {
  let up = 0
  let down = 0
  SIGNALS.forEach(read => {
    const diff = read(after) - read(before)
    if (diff > 0) up += 1
    if (diff < 0) down += 1
  })
  return { up, down }
}

// Did the backfill move anything in-window? Cost/model tools move cost, model count
// or tokens; adoption tools move the activity counters. Used both to end the completion
// poll on real change (not just the timeout) and to classify the outcome below.
export const backfillChanged = (
  before: OrgAiIntegrationUsage,
  after: OrgAiIntegrationUsage
): boolean => {
  const { up, down } = signalDeltas(before, after)
  return up + down > 0
}

// True when the window has no collected rows at all (org inactive / token lapsed
// for that range) — distinct from a genuine $0 window that still has models.
const hasNoData = (u?: OrgAiIntegrationUsage): boolean => {
  const t = u?.totals
  if (!t) return true
  return (
    (t.months ?? 0) === 0 &&
    (t.distinct_users ?? 0) === 0 &&
    cost(u) === 0 &&
    modelCount(u) === 0
  )
}

export const classifyBackfillOutcome = (
  before: OrgAiIntegrationUsage | undefined,
  after: OrgAiIntegrationUsage | undefined,
  // Poll hit cap_seconds with nothing moved: the job may still be running.
  timedOut = false
): BackfillOutcomeResult => {
  const costRecovered = cost(after) - cost(before)
  const { up, down } = signalDeltas(before, after)
  // A fill can show up as cost/models/tokens, or, for credits (Kiro) / interaction-only
  // tools (Amazon Q), as an increase in the activity counters.
  // Fast counts here too: recovering the tier split is cost/token attribution,
  // not the activity-counter wording the other branch describes.
  const costModelsOrTokens =
    costRecovered > 0 ||
    modelCount(after) > modelCount(before) ||
    tokenSum(after) > tokenSum(before) ||
    fastCost(after) > fastCost(before) ||
    fastTokens(after) > fastTokens(before)

  if (up > 0 && down === 0) {
    return {
      outcome: 'filled',
      label: 'Filled',
      tone: 'success',
      description: costModelsOrTokens
        ? 'Historical cost, models and/or tokens were recovered for this window.'
        : 'Historical usage and activity were recovered for this window.',
      // Only ever a positive figure: a models- or tokens-only fill leaves cost flat, and
      // a cost correction alongside a token gain would otherwise persist a negative
      // "recovered" amount to the stored history record.
      costRecovered: Math.max(0, costRecovered)
    }
  }

  // Something moved, but not everything upward: the re-collection restated the window
  // (a lower cost or credits figure replacing an overstated one). The tiles show which
  // metric went which way, so keep the wording generic.
  if (up + down > 0) {
    return {
      outcome: 'recollected',
      label: 'Re-collected',
      tone: costRecovered > 0 ? 'success' : 'neutral',
      description:
        'The backfill re-collected this window and restated its totals; some values ' +
        'decreased. See the before/after figures below.',
      costRecovered: Math.max(0, costRecovered)
    }
  }

  if (timedOut) {
    return {
      outcome: 'unconfirmed',
      label: 'Not confirmed yet',
      tone: 'neutral',
      description:
        'Nothing has changed yet, but the job may still be running. Re-open this ' +
        'result in a few minutes to refresh it.',
      costRecovered: 0
    }
  }

  if (hasNoData(after)) {
    return {
      outcome: 'empty',
      label: 'Empty',
      tone: 'warning',
      description:
        'The job completed but there was nothing to fetch — the org was inactive ' +
        '(or the token lapsed) for this window. Existing rows are unchanged.',
      costRecovered: 0
    }
  }

  if (cost(after) === 0 && modelCount(after) > 0) {
    return {
      outcome: 'filled_zero',
      label: 'Filled ($0)',
      tone: 'neutral',
      description:
        'Genuine no-cost usage — models are present but cost is $0 (e.g. all free ' +
        'code completions). Not an error.',
      costRecovered: 0
    }
  }

  return {
    outcome: 'unchanged',
    label: 'No change',
    tone: 'neutral',
    description:
      "The window's cost and models were already complete — nothing to recover. " +
      'No rows were dropped.',
    costRecovered: 0
  }
}
